import { useQueryClient } from '@tanstack/react-query';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';
import { ArcgisCivil3dCogoPointSettingApiModel } from '../../../../types/models/arcgis-civil3d-cogo-point-setting.model';
import { GetArcgisCivil3dCogoPointSettingApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/cogo-points/get-arcgis-civil3d-cogo-point-setting.request';
import {
  createCogoPointSetting,
  createPropertyMappingForCogoPoint,
  deleteCogoPointSettings,
  deletePropertyMappingFromCogoPoint,
  getAllPropertyMappingsForCogoPoint,
  duplicateCogoPointSettings,
  getCogoPointSetting,
  updateCogoPointSetting,
  updatePropertyMappingsForCogoPoint,
  createPropertyMappingsForCogoPoints,
  searchPropertyMappingsForCogoPoints,
} from '../../api/arcgis-civil3d-cogo-point-setting.api';
import { UpdateArcgisCivil3dCogoPointSettingApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/cogo-points/update-arcgis-civil3d-cogo-point-setting.request';
import { CreateArcgisCivil3dCogoPointSettingApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/cogo-points/create-arcgis-civil3d-cogo-point-setting.request';
import { DeleteArcgisCivil3dCogoPointSettingsApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/cogo-points/delete-arcgis-civil3d-cogo-point-settings.request';
import { CreatePropertyMappingForCogoPointApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/cogo-points/create-property-mapping-for-cogo-point.request';
import { DeletePropertyMappingForCogoPointApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/cogo-points/delete-property-mapping-for-cogo-point.request';
import { GetAllPropertyMappingsForCogoPointApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/cogo-points/get-all-property-mappings-for-cogo-point.request';
import { UpdatePropertyMappingsForCogoPointApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/cogo-points/update-property-mappings-for-cogo-point.request';
import { DuplicateArcgisCivil3dCogoPointsApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/cogo-points/duplicate-arcgis-civil3d-cogo-point-settings.request';
import { CreatePropertyMappingsForCogoPointsApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/cogo-points/create-property-mappings-for-cogo-points.request';
import { SearchPropertyMappingsForCogoPointsApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/cogo-points/search-property-mappings-for-cogo-points.request';

const baseQueryKey = 'cogo-point-setting';
const searchQueryKey = 'search';
const propertyMappingQueryKey = 'property-mappings';

export const useUpdateCogoPointSetting = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateArcgisCivil3dCogoPointSettingApiRequest) =>
      (await updateCogoPointSetting(params)).data,
    onSuccess: (cogoPoint) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === 'search' ||
            (queryKey[1] as UpdateArcgisCivil3dCogoPointSettingApiRequest)?.cogoPointId === cogoPoint?.id),
      }),
  });
  return [request, { ...result, cogoPoint: data }] as const;
};

export const useCreateCogoPointSetting = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateArcgisCivil3dCogoPointSettingApiRequest) =>
      (await createCogoPointSetting(params)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseQueryKey, searchQueryKey] }),
  });
  return [request, { ...result, cogoPoint: data }] as const;
};

export const useGetCogoPointSetting = <T extends ArcgisCivil3dCogoPointSettingApiModel>(
  params: GetArcgisCivil3dCogoPointSettingApiRequest,
) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getCogoPointSetting<T>(params)).data,
    enabled: Boolean(params.cogoPointId && !Number.isNaN(params.cogoPointId)),
  });
  return { ...result, cogoPoint: data } as const;
};

export const useDuplicateCogoPointSettings = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DuplicateArcgisCivil3dCogoPointsApiRequest) =>
      (await duplicateCogoPointSettings(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useDeleteCogoPointSettings = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteArcgisCivil3dCogoPointSettingsApiRequest) =>
      (await deleteCogoPointSettings(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useCreatePropertyMappingForCogoPoint = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreatePropertyMappingForCogoPointApiRequest) =>
      (await createPropertyMappingForCogoPoint(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === propertyMappingQueryKey,
      }),
  });
  return [request, { ...result, ...data }] as const;
};

export const useCreatePropertyMappingsForCogoPoints = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreatePropertyMappingsForCogoPointsApiRequest) =>
      (await createPropertyMappingsForCogoPoints(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, ...data }] as const;
};

export const useSearchPropertyMappingsForCogoPoints = (params: SearchPropertyMappingsForCogoPointsApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) =>
      (await searchPropertyMappingsForCogoPoints({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useUpdatePropertyMappingsForCogoPoint = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdatePropertyMappingsForCogoPointApiRequest) =>
      (await updatePropertyMappingsForCogoPoint(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === propertyMappingQueryKey,
      }),
  });
  return [request, { ...result, ...data }] as const;
};

export const useGetAllPropertyMappingsForCogoPoint = (params: GetAllPropertyMappingsForCogoPointApiRequest) => {
  const { data, ...result } = useAxiosQuery({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getAllPropertyMappingsForCogoPoint(params)).data,
    enabled: Boolean(params.cogoPointId && !Number.isNaN(params.cogoPointId)),
  });
  return { ...result, ...data } as const;
};

export const useDeletePropertyMappingFromCogoPoint = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeletePropertyMappingForCogoPointApiRequest) =>
      (await deletePropertyMappingFromCogoPoint(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === propertyMappingQueryKey,
      }),
  });
  return [request, result] as const;
};
